// Edit-coverage-line modal — FE-SCRM-015.
//
// Producer can move the line's ``quote_status`` and ``outcome``, edit
// incumbent + target-carrier metadata, and delete the row. The
// ``outcome_carrier`` field is conditionally rendered only when the
// chosen outcome is ``won`` — this mirrors the BE check constraint
// (``outcome_carrier IS NULL OR outcome = 'won'``) and the PATCH-side
// auto-clear (flipping outcome off ``won`` wipes the carrier server-
// side, so the UI doesn't leave a stale value on screen).

import { useEffect, useState } from "react";

import { Modal } from "@/design-system";

import {
  COVERAGE_LINE_OUTCOME_LABEL,
  COVERAGE_LINE_TYPE_LABEL,
  QUOTE_STATUS_LABEL,
} from "./labels";
import {
  COVERAGE_LINE_OUTCOMES,
  COVERAGE_LINE_TYPES,
  QUOTE_STATUSES,
  type CoverageLine,
  type CoverageLineOutcome,
  type CoverageLineType,
  type QuoteStatus,
  type UpdateCoverageLineInput,
} from "./types";


interface Props {
  open: boolean;
  initial: CoverageLine | null;
  isSubmitting: boolean;
  isDeleting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (values: UpdateCoverageLineInput) => void;
  onDelete: () => void;
}


export function EditCoverageLineModal({
  open, initial, isSubmitting, isDeleting,
  errorMessage, onClose, onSubmit, onDelete,
}: Props) {
  const [lineType, setLineType] = useState<CoverageLineType>("medical");
  const [currentCarrier, setCurrentCarrier] = useState("");
  const [premiumStr, setPremiumStr] = useState("");
  const [livesStr, setLivesStr] = useState("");
  const [targetCarriers, setTargetCarriers] = useState("");
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>("not_started");
  const [outcome, setOutcome] = useState<CoverageLineOutcome>("pending");
  const [outcomeCarrier, setOutcomeCarrier] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open || !initial) return;
    setLineType((initial.line_type as CoverageLineType) || "medical");
    setCurrentCarrier(initial.current_carrier ?? "");
    setPremiumStr(
      initial.current_premium_annualized_cents !== null
        ? String(initial.current_premium_annualized_cents / 100)
        : "",
    );
    setLivesStr(
      initial.current_lives_covered !== null
        ? String(initial.current_lives_covered)
        : "",
    );
    setTargetCarriers((initial.target_carriers ?? []).join(", "));
    setQuoteStatus((initial.quote_status as QuoteStatus) || "not_started");
    setOutcome((initial.outcome as CoverageLineOutcome) || "pending");
    setOutcomeCarrier(initial.outcome_carrier ?? "");
    setNotes(initial.notes ?? "");
  }, [open, initial]);

  // ``outcome_carrier`` only carries a value when the row's outcome
  // is ``won`` — flipping the outcome away wipes the field locally so
  // the UI never holds onto a value that won't survive a save.
  useEffect(() => {
    if (outcome !== "won") setOutcomeCarrier("");
  }, [outcome]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!initial) return;
    const targets = targetCarriers
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const premiumDollars = premiumStr.trim();
    const premiumCents = premiumDollars
      ? Math.round(Number(premiumDollars) * 100)
      : null;
    const livesTrim = livesStr.trim();
    const lives = livesTrim ? Number(livesTrim) : null;
    onSubmit({
      line_type: lineType,
      current_carrier: currentCarrier.trim() || null,
      current_premium_annualized_cents:
        Number.isFinite(premiumCents) ? premiumCents : null,
      current_lives_covered:
        lives !== null && Number.isFinite(lives) ? lives : null,
      target_carriers: targets,
      quote_status: quoteStatus,
      outcome,
      outcome_carrier:
        outcome === "won" ? outcomeCarrier.trim() || null : null,
      notes: notes.trim() || null,
    });
  }

  const title = initial
    ? `Edit ${COVERAGE_LINE_TYPE_LABEL[initial.line_type as CoverageLineType] ?? initial.line_type}`
    : "Edit coverage line";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onDelete}
            disabled={isSubmitting || isDeleting}
            data-testid="edit-coverage-line-delete"
            className="mr-auto rounded-md border border-ember/40 px-3 py-1.5 text-sm text-ember hover:bg-ember/10 disabled:opacity-60"
          >
            {isDeleting ? "Removing…" : "Remove"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting || isDeleting}
            data-testid="edit-coverage-line-cancel"
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-coverage-line-form"
            disabled={isSubmitting || isDeleting}
            data-testid="edit-coverage-line-submit"
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {isSubmitting ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <form
        id="edit-coverage-line-form"
        data-testid="edit-coverage-line-form"
        onSubmit={submit}
        className="flex flex-col gap-3"
      >
        {errorMessage && (
          <div
            role="alert"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember"
          >
            {errorMessage}
          </div>
        )}

        <Field label="Coverage type">
          <select
            data-testid="edit-coverage-line-type"
            value={lineType}
            onChange={(e) => setLineType(e.target.value as CoverageLineType)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          >
            {COVERAGE_LINE_TYPES.map((t) => (
              <option key={t} value={t}>
                {COVERAGE_LINE_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Current carrier (incumbent)">
          <input
            data-testid="edit-coverage-line-current-carrier"
            type="text"
            value={currentCarrier}
            onChange={(e) => setCurrentCarrier(e.target.value)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Annual premium ($)">
            <input
              data-testid="edit-coverage-line-premium"
              type="number"
              min="0"
              step="1"
              value={premiumStr}
              onChange={(e) => setPremiumStr(e.target.value)}
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Lives covered">
            <input
              data-testid="edit-coverage-line-lives"
              type="number"
              min="0"
              step="1"
              value={livesStr}
              onChange={(e) => setLivesStr(e.target.value)}
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            />
          </Field>
        </div>

        <Field
          label="Target carriers"
          hint="Comma-separated list of carriers you're quoting."
        >
          <input
            data-testid="edit-coverage-line-target-carriers"
            type="text"
            value={targetCarriers}
            onChange={(e) => setTargetCarriers(e.target.value)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Quote status">
            <select
              data-testid="edit-coverage-line-quote-status"
              value={quoteStatus}
              onChange={(e) => setQuoteStatus(e.target.value as QuoteStatus)}
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            >
              {QUOTE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {QUOTE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Outcome">
            <select
              data-testid="edit-coverage-line-outcome"
              value={outcome}
              onChange={(e) =>
                setOutcome(e.target.value as CoverageLineOutcome)
              }
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            >
              {COVERAGE_LINE_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {COVERAGE_LINE_OUTCOME_LABEL[o]}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {outcome === "won" ? (
          <Field
            label="Carrier they went with"
            hint="The carrier the prospect chose for this line."
          >
            <input
              data-testid="edit-coverage-line-outcome-carrier"
              type="text"
              value={outcomeCarrier}
              onChange={(e) => setOutcomeCarrier(e.target.value)}
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            />
          </Field>
        ) : null}

        <Field label="Notes">
          <textarea
            data-testid="edit-coverage-line-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>
      </form>
    </Modal>
  );
}


function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">{label}</span>
      {children}
      {hint ? (
        <span className="text-xs text-text-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}
